import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Edit, Trash2, ArrowLeft, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { customerAPI } from '../services/api';
import useAuthStore from '../stores/authStore';
import { useDateFormat } from '../hooks/useDateFormat';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: customer, isLoading } = useQuery(
    ['customer', id],
    () => customerAPI.getOne(id).then(res => res.data.data)
  );

  const deleteMutation = useMutation(() => customerAPI.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('customers');
      queryClient.invalidateQueries('statistics');
      toast.success('Customer deleted successfully');
      navigate('/customers');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete customer');
    }
  });

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${customer?.customerName}"? This action cannot be undone.`)) {
      deleteMutation.mutate();
    }
  };

  const { formatDate } = useDateFormat();

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="max-w-6xl space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <button onClick={() => navigate(-1)} className="btn btn-secondary w-fit">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold break-words">{customer?.customerName}</h1>
      </div>

      {/* Contact Information */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-6">
          <h2 className="text-lg md:text-xl font-bold">Customer Information</h2>
          <div className="flex gap-2">
            <Link to={`/customers/${id}/edit`} className="btn btn-secondary flex-1 sm:flex-none justify-center">
              <Edit size={18} />
              <span className="ml-2 sm:hidden">Edit</span>
            </Link>
            {user?.role === 'admin' && (
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="btn btn-danger flex-1 sm:flex-none justify-center"
              >
                <Trash2 size={18} />
                <span className="ml-2 sm:hidden">{deleteMutation.isLoading ? 'Deleting...' : 'Delete'}</span>
              </button>
            )}
          </div>
        </div>

        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <dt className="text-sm font-medium text-gray-600">Phone</dt>
            <dd className="mt-1">
              {customer?.phone ? (
                <a href={`tel:${customer.phone}`} className="text-primary-600 hover:text-primary-800 hover:underline">
                  {customer.phone}
                </a>
              ) : '-'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-600">Email</dt>
            <dd className="mt-1">
              {customer?.email ? (
                <a href={`mailto:${customer.email}`} className="text-primary-600 hover:text-primary-800 hover:underline">
                  {customer.email}
                </a>
              ) : '-'}
            </dd>
          </div>
        </dl>
      </div>

      {/* Additional Notes */}
      {customer?.remarks && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Additional Notes</h2>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 whitespace-pre-wrap">{customer.remarks}</p>
          </div>
        </div>
      )}

      {/* Address & Location */}
      <div className="card">
        <h2 className="text-lg md:text-xl font-bold mb-4 md:mb-6">Address & Location</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <div className="md:col-span-2">
            <dt className="text-sm font-medium text-gray-600">Full Address</dt>
            <dd className="mt-1 text-gray-900">{customer?.fullAddress || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <MapPin size={16} />
              GPS Coordinates (Pin Location)
            </dt>
            <dd className="mt-1">
              {customer?.latitude && customer?.longitude ? (
                <Link
                  to={`/map?lat=${customer.latitude}&lng=${customer.longitude}&customerId=${customer.id}`}
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium flex items-center gap-1"
                >
                  <MapPin size={14} />
                  {customer.pinLocation || `${customer.latitude}, ${customer.longitude}`}
                </Link>
              ) : (
                <span className="text-gray-900">-</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-600">Area</dt>
            <dd className="mt-1 text-gray-900">{customer?.area || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-600">Status</dt>
            <dd className="mt-1">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                customer?.status === 'Done' ? 'bg-green-100 text-green-800' :
                customer?.status === 'Pending Installation' ? 'bg-blue-100 text-blue-800' :
                customer?.status === 'In Process' ? 'bg-yellow-100 text-yellow-800' :
                customer?.status === 'Canceled' ? 'bg-red-100 text-red-800' :
                customer?.status === 'Standby' ? 'bg-purple-100 text-purple-800' :
                customer?.status === 'Future Fiber' ? 'bg-cyan-100 text-cyan-800' :
                customer?.status === 'Reminder' ? 'bg-pink-100 text-pink-800' :
                customer?.status === 'Suspended' ? 'bg-red-100 text-red-800' :
                customer?.status === 'Temporary Disconnected' ? 'bg-orange-100 text-orange-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {customer?.status}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      {/* CYTA Fiber Details */}
      <div className="card">
        <h2 className="text-lg md:text-xl font-bold mb-4 md:mb-6 text-green-700">CYTA Fiber Details</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <div className="md:col-span-2">
            <dt className="text-sm font-medium text-gray-600">CYTA Fiber Code</dt>
            <dd className="mt-1 text-gray-900 font-mono text-sm">{customer?.cytaFiberCode || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-600">CYTA Reference Number</dt>
            <dd className="mt-1 text-gray-900">{customer?.cytaReference || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-600">CYTA Circuit Number</dt>
            <dd className="mt-1 text-gray-900">
              {customer?.cytaCircuitNumber ? (
                <Link
                  to={`/cyta-api?circuit=${parseInt(customer.cytaCircuitNumber)}`}
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                >
                  {parseInt(customer.cytaCircuitNumber)}
                </Link>
              ) : '-'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-600">GPON Serial</dt>
            <dd className="mt-1 text-gray-900 font-mono text-sm">{customer?.gponSerial || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-600">CYTA Plan</dt>
            <dd className="mt-1 text-gray-900">{customer?.cytaPlan || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-600">Request Date</dt>
            <dd className="mt-1 text-gray-900">{formatDate(customer?.requestDate)}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-600">Installation Date</dt>
            <dd className="mt-1 text-gray-900">{formatDate(customer?.installationDate)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
};

export default CustomerDetail;
